<template>
  <div class="tool-page">
    <nav class="breadcrumb">
      <router-link to="/">{{ $t('nav.home') }}</router-link>
      <span class="separator">›</span>
      <span class="current">{{ $t('colorConverter.title') }}</span>
    </nav>

    <div class="tool-header">
      <h1>{{ $t('colorConverter.title') }}</h1>
      <p>{{ $t('colorConverter.subtitle') }}</p>
    </div>

    <div class="tool-card">
      <div class="color-preview-bar" :style="{ background: previewColor }"></div>

      <div class="color-inputs">
        <div class="color-input-group">
          <label>HEX</label>
          <input v-model="hex" @input="fromHex" spellcheck="false" placeholder="#FF5733" class="mono-input" />
        </div>
        <div class="color-input-group">
          <label>RGB</label>
          <div class="multi-input">
            <input v-model.number="r" type="number" min="0" max="255" @input="fromRgb" placeholder="R" />
            <input v-model.number="g" type="number" min="0" max="255" @input="fromRgb" placeholder="G" />
            <input v-model.number="b" type="number" min="0" max="255" @input="fromRgb" placeholder="B" />
          </div>
        </div>
        <div class="color-input-group">
          <label>HSL</label>
          <div class="multi-input">
            <input v-model.number="hue" type="number" min="0" max="360" @input="fromHsl" placeholder="H" />
            <input v-model.number="sat" type="number" min="0" max="100" @input="fromHsl" placeholder="S%" />
            <input v-model.number="light" type="number" min="0" max="100" @input="fromHsl" placeholder="L%" />
          </div>
        </div>
        <div class="color-input-group">
          <label>RGBA</label>
          <div class="multi-input">
            <input v-model.number="r" type="number" min="0" max="255" @input="fromRgb" placeholder="R" />
            <input v-model.number="g" type="number" min="0" max="255" @input="fromRgb" placeholder="G" />
            <input v-model.number="b" type="number" min="0" max="255" @input="fromRgb" placeholder="B" />
            <input v-model.number="alpha" type="number" min="0" max="1" step="0.1" @input="fromRgb" placeholder="A" />
          </div>
        </div>
      </div>

      <div class="color-strings">
        <div class="color-string-item" v-for="item in colorStrings" :key="item.label">
          <span class="cs-label">{{ item.label }}</span>
          <code class="cs-value">{{ item.value }}</code>
          <button class="copy-btn" @click="copy(item.value)">📋</button>
        </div>
      </div>

      <div v-if="successMessage" class="alert success">{{ successMessage }}</div>

      <div class="picker-section">
        <label>{{ $t('colorConverter.pickerLabel') }}</label>
        <input type="color" v-model="pickerColor" @input="fromPicker" class="color-picker" />
      </div>
    </div>

    <div class="tool-description">
      <h2>{{ $t('colorConverter.whatIs') }}</h2>
      <p>{{ $t('colorConverter.whatIsDesc') }}</p>
      <h2>{{ $t('colorConverter.formatsTitle') }}</h2>
      <ul>
        <li v-for="(item, i) in $t('colorConverter.formats')" :key="i">{{ item }}</li>
      </ul>
      <h2>{{ $t('colorConverter.tipsTitle') }}</h2>
      <ul>
        <li v-for="(item, i) in $t('colorConverter.tips')" :key="i">{{ item }}</li>
      </ul>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ColorConverter',
  data() {
    return {
      hex: '#FF5733',
      r: 255, g: 87, b: 51,
      hue: 9, sat: 100, light: 60,
      alpha: 1,
      pickerColor: '#FF5733',
      successMessage: ''
    }
  },
  computed: {
    previewColor() {
      return `rgba(${this.r}, ${this.g}, ${this.b}, ${this.alpha})`
    },
    colorStrings() {
      return [
        { label: 'HEX', value: this.hex },
        { label: 'RGB', value: `rgb(${this.r}, ${this.g}, ${this.b})` },
        { label: 'RGBA', value: `rgba(${this.r}, ${this.g}, ${this.b}, ${this.alpha})` },
        { label: 'HSL', value: `hsl(${this.hue}, ${this.sat}%, ${this.light}%)` },
        { label: 'HSLA', value: `hsla(${this.hue}, ${this.sat}%, ${this.light}%, ${this.alpha})` }
      ]
    }
  },
  methods: {
    fromHex() {
      let h = this.hex.replace(/^#/, '')
      if (h.length === 3) h = h.split('').map(c => c + c).join('')
      if (!/^[0-9a-fA-F]{6}$/.test(h)) return
      this.r = parseInt(h.substr(0, 2), 16)
      this.g = parseInt(h.substr(2, 2), 16)
      this.b = parseInt(h.substr(4, 2), 16)
      this.pickerColor = '#' + h
      this.updateHsl()
    },
    fromRgb() {
      this.hex = '#' + [this.r, this.g, this.b].map(v => Math.max(0, Math.min(255, v || 0)).toString(16).padStart(2, '0')).join('').toUpperCase()
      this.pickerColor = this.hex
      this.updateHsl()
    },
    fromHsl() {
      const h = this.hue / 360, s = this.sat / 100, l = this.light / 100
      let r, g, b
      if (s === 0) { r = g = b = l } else {
        const hue2rgb = (p, q, t) => { if (t < 0) t += 1; if (t > 1) t -= 1; if (t < 1/6) return p + (q - p) * 6 * t; if (t < 1/2) return q; if (t < 2/3) return p + (q - p) * (2/3 - t) * 6; return p }
        const q = l < 0.5 ? l * (1 + s) : l + s - l * s, p = 2 * l - q
        r = hue2rgb(p, q, h + 1/3); g = hue2rgb(p, q, h); b = hue2rgb(p, q, h - 1/3)
      }
      this.r = Math.round(r * 255); this.g = Math.round(g * 255); this.b = Math.round(b * 255)
      this.hex = '#' + [this.r, this.g, this.b].map(v => v.toString(16).padStart(2, '0')).join('').toUpperCase()
      this.pickerColor = this.hex
    },
    fromPicker() {
      this.hex = this.pickerColor.toUpperCase()
      this.fromHex()
    },
    updateHsl() {
      const r = this.r / 255, g = this.g / 255, b = this.b / 255
      const max = Math.max(r, g, b), min = Math.min(r, g, b)
      let h = 0, s = 0, l = (max + min) / 2
      if (max !== min) {
        const d = max - min
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
        if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6
        else if (max === g) h = ((b - r) / d + 2) / 6
        else h = ((r - g) / d + 4) / 6
      }
      this.hue = Math.round(h * 360); this.sat = Math.round(s * 100); this.light = Math.round(l * 100)
    },
    async copy(text) {
      try {
        await navigator.clipboard.writeText(text)
        this.successMessage = this.$t('common.copied')
      } catch {
        const ta = document.createElement('textarea')
        ta.value = text; ta.style.position = 'fixed'; ta.style.left = '-9999px'
        document.body.appendChild(ta); ta.select(); document.execCommand('copy'); document.body.removeChild(ta)
        this.successMessage = this.$t('common.copied')
      }
      setTimeout(() => { this.successMessage = '' }, 2000)
    }
  },
  mounted() {
    this.fromHex()
  }
}
</script>

<style scoped>
.tool-page { max-width: 1200px; margin: 0 auto; }
.breadcrumb { margin-bottom: 24px; font-size: 14px; color: var(--text-secondary); }
.breadcrumb a { color: var(--accent-color); text-decoration: none; }
.breadcrumb a:hover { text-decoration: underline; }
.breadcrumb .separator { margin: 0 8px; color: var(--text-muted); }
.tool-header { text-align: center; margin-bottom: 32px; }
.tool-header h1 { font-size: 32px; margin-bottom: 12px; color: var(--text-primary); }
.tool-header p { color: var(--text-secondary); font-size: 16px; max-width: 600px; margin: 0 auto; line-height: 1.6; }
.tool-card { background: var(--bg-card); border: 1px solid var(--border-color); border-radius: 16px; padding: 24px; box-shadow: var(--shadow-md); }
.color-preview-bar { height: 80px; border-radius: 12px; margin-bottom: 20px; border: 1px solid var(--border-color); background-image: linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%); background-size: 16px 16px; background-position: 0 0, 0 8px, 8px -8px, -8px 0px; }
.color-inputs { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 16px; margin-bottom: 20px; }
.color-input-group label { font-size: 13px; color: var(--text-secondary); font-weight: 600; margin-bottom: 6px; display: block; }
.color-input-group .mono-input { width: 100%; background: var(--bg-input); border: 1px solid var(--border-color); border-radius: 8px; padding: 10px 14px; color: var(--text-primary); font-family: monospace; font-size: 15px; box-sizing: border-box; outline: none; }
.color-input-group .mono-input:focus { border-color: var(--accent-color); }
.multi-input { display: flex; gap: 8px; }
.multi-input input { flex: 1; background: var(--bg-input); border: 1px solid var(--border-color); border-radius: 8px; padding: 10px; color: var(--text-primary); font-family: monospace; font-size: 14px; text-align: center; outline: none; box-sizing: border-box; min-width: 0; }
.multi-input input:focus { border-color: var(--accent-color); }
.color-strings { display: flex; flex-direction: column; gap: 8px; margin-bottom: 20px; }
.color-string-item { display: flex; align-items: center; gap: 12px; padding: 10px 14px; background: var(--bg-tertiary); border-radius: 8px; }
.cs-label { font-size: 12px; color: var(--text-secondary); font-weight: 600; min-width: 48px; }
.cs-value { flex: 1; font-family: monospace; font-size: 14px; color: var(--text-primary); }
.copy-btn { background: none; border: none; cursor: pointer; font-size: 14px; padding: 2px 6px; border-radius: 4px; transition: background 0.2s; }
.copy-btn:hover { background: var(--bg-hover); }
.picker-section { display: flex; align-items: center; gap: 12px; }
.picker-section label { font-size: 14px; color: var(--text-secondary); }
.color-picker { width: 60px; height: 40px; border: none; cursor: pointer; border-radius: 8px; }
.alert.success { padding: 12px 16px; border-radius: 8px; margin-bottom: 16px; background: rgba(34,197,94,0.1); border: 1px solid rgba(34,197,94,0.3); color: var(--success-color); }
.tool-description { margin-top: 32px; background: var(--bg-card); border: 1px solid var(--border-color); border-radius: 16px; padding: 32px; box-shadow: var(--shadow-sm); }
.tool-description h2 { font-size: 20px; color: var(--text-primary); margin: 24px 0 16px; padding-bottom: 8px; border-bottom: 2px solid var(--accent-light); }
.tool-description h2:first-child { margin-top: 0; }
.tool-description p { font-size: 15px; color: var(--text-secondary); line-height: 1.8; margin-bottom: 16px; }
.tool-description ul { padding-left: 24px; margin-bottom: 16px; }
.tool-description li { font-size: 15px; color: var(--text-secondary); line-height: 1.8; margin-bottom: 8px; }
@media (max-width: 768px) { .color-inputs { grid-template-columns: 1fr; } }
</style>
